import React, { useContext, useEffect, useState } from "react";
import AppContext from "../../../AppContext";
import { formatDate } from "@telerik/kendo-intl";
import { ReactSVG } from "react-svg";
import Details from "../Details";
import Graphs from "../Graphs";
import AlarmHistoryTable from "../AlarmHistoryTable";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";
import AssetMap from "../AssetMap";
import { timeModes } from "../../../constant";
import { TimeModeSelector } from "./TimeModeSelecter";
import { ExpandButton } from "./ExpandButton";

const AssetView = ({
  expanded,
  goBackClick,
  assetData,
  t,
  onDetailsConfig,
  setExpanded,
  momentData,
  currentPlayTime,
  detailsRef,
  tableRef,
  mapRef,
  autoCenter,
  setAutoCenter,
  mode,
  setMode,
  graphRef,
  dateRange,
  alarmLines,
  setAlarmLines,
  assetType,
  loadedAssetIsChild,
}) => {
  const { getTimeZoneDateTime, playbackMode, darkMode, config } =
    useContext(AppContext);
  const [assetConfig, setAssetConfig] = useState({ graph: [], detail: [] });

  const loadConfiguration = () => {
    if (config && config.assetDetails && assetData) {
      const getDetailConfig =
        config?.assetDetails?.detailsConfiguration?.assetParameters?.find(
          (x) => x.assetId === assetData.id
        );
      const getGraphConfig =
        config?.assetDetails?.graphConfiguration?.assetParameters?.find(
          (x) => x.assetId === assetData.id
        );
      setAssetConfig({
        detail: getDetailConfig
          ? {
              parameters: getDetailConfig.parameters,
              showLinkedDevice:
                config?.assetDetails?.detailsConfiguration.showLinkedDevice,
            }
          : null,
        graph: getGraphConfig ? getGraphConfig.parameters : [],
      });
    }
  };

  useEffect(() => {
    loadConfiguration();
  }, [config]);

  return (
    <div
      className={`${
        expanded !== null ? " h-full" : "max-h-full"
      } flex flex-wrap w-full`}
    >
      {/* Asset Details Section */}
      <div
        className={`${
          expanded === "detail"
            ? "h-full w-full"
            : expanded === null
            ? "md:w-1/2 w-full h-fit min-h-40vh md:h-1/2 pt-1"
            : "hidden"
        } transition-all ease-in-out `}
      >
        <div className="flex px-4 pl-1 pb-2 dark:text-white text-black justify-between">
          <div className="flex gap-3 items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2.0"
              stroke="currentColor"
              className="h-4 lg:h-7 w-4 lg:w-7"
              onClick={goBackClick}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
              />
            </svg>
            <label className="md:text-xs text-xxs lg:text-lg font-semibold">
              {assetData?.name}
            </label>
          </div>
          <div className="flex items-center dark:text-stone-300 text-black md:text-xxs text-xxxs">
            {playbackMode === 100 && (
              <div className="flex items-center gap-3 border-r h-5 pr-3">
                {t("assetDetails.lastDataTimeLabel")}: [&nbsp;
                {assetData &&
                  assetData?.timeStamp &&
                  formatDate(
                    getTimeZoneDateTime(assetData.timeStamp),
                    "dd-MMM-yyyy hh:mm:ss a"
                  )}
                &nbsp;]
              </div>
            )}
            <div className="flex items-center gap-3 ml-3">
              {playbackMode !== 200 && (
                <button
                  type="button"
                  className="md:w-4 w-3 lg:w-5"
                  onClick={() => {
                    // setConfigType();
                    onDetailsConfig("detailsConfiguration");
                  }}
                >
                  {/* <img src="EditIcon.svg" alt="EditIcon" className="w-fit" /> */}
                  <ReactSVG
                    src="EditIcon.svg"
                    className="h-9 flex items-center justify-center"
                    beforeInjection={(svg) => {
                      svg.setAttribute(
                        "style",
                        `stroke: ${darkMode ? "white" : "black"}`
                      );
                    }}
                  />
                </button>
              )}
              <ExpandButton
                buttonType={"detail"}
                expanded={expanded}
                setExpanded={setExpanded}
                darkMode={darkMode}
              />
            </div>
          </div>
        </div>
        <Details
          assetData={assetData}
          detailsPreference={config}
          expanded={expanded}
          momentData={momentData}
          playBackMode={playbackMode}
          currentPlayTime={currentPlayTime}
          ref={detailsRef}
          assetConfig={assetConfig.detail}
          assetType={assetType}
          loadedAssetIsChild={loadedAssetIsChild}
        />
      </div>{" "}
      {/* Graphs Section */}
      <div
        className={`${
          expanded === "graph"
            ? "h-[77vh]"
            : expanded === null
            ? "md:w-1/2 h-96 md:h-1/2 md:pl-4 pt-1"
            : "hidden"
        } dark:bg-neutral-900 bg-white md:mb-3 rounded-xl w-full`}
      >
        <div className="flex px-4 dark:text-stone-300 text-black justify-between">
          <div className="flex md:gap-3 gap-1 items-center">
            <label className="md:text-xs text-xxs font-medium min-w-28">
              {t("assetDetails.graph.headerText")}
            </label>
            {playbackMode !== 200 && (
              <TimeModeSelector
                timeModes={timeModes}
                selectedMode={mode.graph}
                onChange={(newMode) =>
                  setMode((pre) => ({ ...pre, graph: newMode }))
                }
                disabled={playbackMode === 200}
              />
            )}
          </div>
          <div className="flex items-center md:gap-3 gap-1">
            <div className="flex items-center font-semibold md:text-xxs text-xxxs">
              <button
                className={`w-full ${
                  alarmLines
                    ? "bg-purple-700"
                    : "dark:bg-white bg-zinc-300 text-black"
                } rounded-lg md:px-2 px-1 py-1`}
                onClick={() => setAlarmLines(!alarmLines)}
              >
                {t("assetDetails.graph.alarmLineToggleBtnText")}
              </button>
            </div>
            {playbackMode !== 200 && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="md:w-5 w-3"
                  onClick={() => {
                    // setConfigType();
                    onDetailsConfig("graphConfiguration");
                  }}
                >
                  {/* <img src="EditIcon.svg" alt="EditIcon" className="w-fit" /> */}
                  <ReactSVG
                    src="EditIcon.svg"
                    className="w-4 flex items-center justify-center"
                    beforeInjection={(svg) => {
                      svg.setAttribute(
                        "style",
                        `stroke: ${darkMode ? "white" : "black"}`
                      );
                    }}
                  />
                </button>
              </div>
            )}
            <ExpandButton
              buttonType={"graph"}
              expanded={expanded}
              setExpanded={setExpanded}
              darkMode={darkMode}
            />
          </div>
        </div>
        <div
          className={`${
            expanded === "graph" ? "h-full" : " h-5/6"
          } transition-all ease-in-out `}
        >
          {assetData && config && assetConfig.graph.length > 0 ? (
            <Graphs
              detailsPreference={config}
              assetData={assetData}
              mode={mode}
              playBackDateRange={dateRange}
              alarmLines={alarmLines}
              momentData={momentData}
              setMode={setMode}
              ref={graphRef}
              assetConfig={assetConfig.graph}
              assetId={assetData.id}
            />
          ) : (
            <div className="text-slate-600 flex h-full items-center justify-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
              {t("assetDetails.graph.configWarningText")}
              <img
                src="EditIcongray.svg"
                alt="EditIconGray"
                className="w-5 text-black"
              />
            </div>
          )}
        </div>
      </div>{" "}
      {/* Alarm History Section */}
      <div
        className={`${
          expanded === "table"
            ? "h-full w-full"
            : expanded === null
            ? "w-full md:w-1/2 h-80 md:h-[45vh]"
            : "hidden"
        } transition-all ease-in-out dark:bg-neutral-900 bg-[#9D9DA1] rounded-xl mix-blend-normal`}
      >
        <div className="flex md:px-4 px-1 py-3 dark:text-stone-300 text-black justify-between items-center gap-1">
          <div className="flex gap-3 items-center">
            <label className="md:text-xs text-xxs font-medium w-fit">
              {t("assetDetails.table.headerText")} {assetData?.name}
            </label>
            {playbackMode !== 200 && (
              <TimeModeSelector
                timeModes={timeModes}
                selectedMode={mode.table}
                onChange={(newMode) =>
                  setMode((pre) => ({ ...pre, table: newMode }))
                }
                disabled={playbackMode === 200}
              />
            )}
          </div>
          <ExpandButton
            buttonType={"table"}
            expanded={expanded}
            setExpanded={setExpanded}
            darkMode={darkMode}
          />
        </div>
        {assetData && (
          <AlarmHistoryTable
            asset={assetData}
            expanded={expanded}
            momentData={momentData}
            playBackMode={playbackMode}
            mode={mode}
            setMode={setMode}
            ref={tableRef}
            assetId={assetData.id}
          />
        )}
      </div>
      {/* Map Section */}
      <div
        className={`${
          expanded === null
            ? "w-screen md:w-1/2 h-80 md:py-0 py-4 md:h-[41%] md:pl-4"
            : expanded === "map"
            ? "h-[80vh] w-full"
            : "hidden"
        } transition-all ease-in-out duration-150 flex-col items-end relative`}
      >
        <div className="flex px-4 pb-1 dark:text-stone-300 text-black justify-between items-center">
          <div>
            <label className="text-xs font-medium w-56 mr-4">
              {t("assetDetails.map.headerText")}
            </label>
            {playbackMode !== 200 && (
              <TimeModeSelector
                timeModes={timeModes}
                selectedMode={mode.map}
                onChange={(newMode) =>
                  setMode((pre) => ({ ...pre, map: newMode }))
                }
                disabled={playbackMode === 200}
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={` md:text-xs rounded-sm ${
                autoCenter ? " bg-blue-500" : "bg-white"
              } text-xxs font-medium text-center w-fit px-1 h-fit flex justify-center items-center gap-0`}
              onClick={() => setAutoCenter(!autoCenter)}
            >
              <TooltipComponent
                content={
                  autoCenter
                    ? t("home.map.autoCenterOn")
                    : t("home.map.autoCenterOff")
                }
                delayUpdate={false}
                key={"autoCenterToggle"}
                immediateRender
                opensOn="Hover"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="24px"
                  viewBox="0 0 24 24"
                  width="18px"
                  fill={autoCenter ? "white" : "#5f6368"}
                >
                  <path d="M0 0h24v24H0z" fill="none" />
                  <path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3-8c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3 3 1.34 3 3z" />
                </svg>
              </TooltipComponent>
            </button>

            <ExpandButton
              buttonType={"map"}
              expanded={expanded}
              setExpanded={setExpanded}
              darkMode={darkMode}
            />
          </div>
        </div>
        {assetData && (
          <AssetMap
            mode={mode}
            expanded={expanded}
            assetData={assetData}
            detailsPreference={config}
            playBackMode={playbackMode}
            momentData={momentData}
            setAutoCenter={setAutoCenter}
            autoCenter={autoCenter}
            ref={mapRef}
          />
        )}
      </div>
    </div>
  );
};

export default AssetView;
